import React from 'react';
import { useAiStatus, useAiSummary, useRegenerateSummary } from './useAiSummary';

/**
 * AiSummaryPanel — Plug-and-play AI Submission Summary component.
 *
 * Drop this into any review page:
 *   <AiSummaryPanel assignmentId={assignmentId} />
 *
 * Behaviour:
 *  - If the feature is disabled (ai.summary.enabled: false), renders nothing.
 *  - On first render, auto-generates a summary via Claude and caches it.
 *  - Shows a "Regenerate" button for reviewers to refresh the summary.
 *  - Degrades gracefully on API errors — shows a retry message, never crashes.
 *
 * @param {{ assignmentId: number|string }} props
 */
export default function AiSummaryPanel({ assignmentId }) {
  // 1. Check if the feature is on
  const { data: statusData, isLoading: statusLoading } = useAiStatus();
  const featureEnabled = statusData?.data?.enabled === true;

  // 2. Fetch the summary (only runs when feature is enabled)
  const {
    data: summaryData,
    isLoading: summaryLoading,
    isError,
    refetch,
  } = useAiSummary(assignmentId, featureEnabled);

  // 3. Regenerate mutation
  const { mutate: regenerate, isLoading: regenerating } = useRegenerateSummary(assignmentId);

  // ── Guard: don't render if feature is off or still checking ───────────────
  if (statusLoading) return null;
  if (!featureEnabled) return null;
  if (!assignmentId)  return null;

  const summary      = summaryData?.data?.summary;
  const generatedAt  = summaryData?.data?.generatedAt;
  const modelUsed    = summaryData?.data?.modelUsed;
  const isGenerating = summaryLoading || regenerating;

  return (
    <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-xl border border-violet-200 p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">✨</span>
          <h3 className="font-semibold text-violet-900 text-sm">AI Summary</h3>
          <span className="text-xs text-violet-400 bg-violet-100 px-2 py-0.5 rounded-full">
            {modelUsed ? 'claude' : 'OmniDrive AI'}
          </span>
        </div>

        {/* Regenerate button — only shown when a summary already exists */}
        {summary && !isGenerating && (
          <button
            onClick={() => regenerate()}
            className="text-xs text-violet-600 hover:text-violet-800 border border-violet-300 hover:border-violet-500 px-2.5 py-1 rounded-lg transition-colors"
          >
            ↺ Regenerate
          </button>
        )}
      </div>

      {/* Content area */}
      {isGenerating ? (
        <GeneratingSkeleton />
      ) : isError ? (
        <ErrorState onRetry={refetch} />
      ) : summary ? (
        <SummaryContent summary={summary} generatedAt={generatedAt} />
      ) : (
        <EmptyState onGenerate={refetch} />
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function GeneratingSkeleton() {
  return (
    <div className="space-y-2 animate-pulse">
      <div className="h-3 bg-violet-200 rounded-full w-full" />
      <div className="h-3 bg-violet-200 rounded-full w-5/6" />
      <div className="h-3 bg-violet-200 rounded-full w-4/5" />
      <div className="h-3 bg-violet-200 rounded-full w-3/4" />
      <p className="text-xs text-violet-400 mt-2 text-center">Generating summary…</p>
    </div>
  );
}

function SummaryContent({ summary, generatedAt }) {
  const formattedDate = generatedAt
    ? new Date(generatedAt).toLocaleString('en-US', {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <div>
      <p className="text-sm text-gray-700 leading-relaxed">{summary}</p>
      {formattedDate && (
        <p className="text-xs text-violet-400 mt-3">Generated {formattedDate}</p>
      )}
    </div>
  );
}

function ErrorState({ onRetry }) {
  return (
    <div className="text-center py-2">
      <p className="text-sm text-red-500">Could not generate summary.</p>
      <button
        onClick={onRetry}
        className="text-xs text-violet-600 hover:underline mt-1"
      >
        Try again
      </button>
    </div>
  );
}

function EmptyState({ onGenerate }) {
  return (
    <div className="text-center py-2">
      <p className="text-sm text-violet-500">No summary yet.</p>
      <button
        onClick={onGenerate}
        className="text-xs text-violet-600 hover:underline mt-1"
      >
        Generate now
      </button>
    </div>
  );
}
